// frontend/src/services/api.js - COMPLETE LIVE SERVER VERSION
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('erp_auth_token') || 'demo-admin-token';
    
    if (token && token !== 'demo-admin-token') {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add logging for debugging
    if (process.env.NODE_ENV !== 'production') {
      console.log(`📤 API Request: ${config.method.toUpperCase()} ${config.url}`, config.data || '');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Add logging for debugging
    if (process.env.NODE_ENV !== 'production') {
      console.log(`📥 API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response.data;
  },
  (error) => {
    console.error('❌ API Error:', error.config?.url, error.response?.status);
    
    if (error.response) {
      const errorData = {
        success: false,
        message: error.response.data?.message || `Server error: ${error.response.status}`,
        error: error.response.data?.error || error.response.statusText,
        status: error.response.status,
        timestamp: new Date().toISOString()
      };
      
      if (error.response.status === 401) {
        localStorage.removeItem('erp_auth_token');
        localStorage.removeItem('erp_user');
        window.location.href = '/login';
        errorData.message = 'Session expired. Please login again.';
      }
      
      if (error.response.status === 404) {
        errorData.message = 'Resource not found. Please check the URL.';
      }
      
      return Promise.reject(errorData);
    } else if (error.request) {
      return Promise.reject({
        success: false,
        message: 'Network error. Please check your connection.',
        error: 'No response from server',
        timestamp: new Date().toISOString()
      });
    } else {
      return Promise.reject({
        success: false,
        message: 'Request failed. Please try again.',
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  }
);

// ==================== TEST & DEBUG API ====================
export const testConnection = () => api.get('/test');
export const testPOSConnection = () => api.get('/pos/test');
export const debugAPI = (data) => api.post('/pos-debug', data);

// ==================== DASHBOARD API ====================
export const getDashboardStats = () => api.get('/dashboard-stats');

// ==================== POS API ====================
export const getPOS = (query = '') => api.get(`/pos${query}`);
export const getPOSById = (id) => api.get(`/pos/${id}`);

// Create POS with proper data structure
export const createPOS = (posData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Sending POS data to backend:', JSON.stringify(posData, null, 2));
  }
  return api.post('/pos', posData);
};

export const refundPOS = (id, refundData) => api.post(`/pos/${id}/refund`, refundData);
export const voidPOS = (id, reason) => api.put(`/pos/${id}/void`, { reason });
export const getPOSStats = () => api.get('/pos/stats');
export const searchSerialNumbers = (query) => api.get(`/pos/search/serial${query}`);
export const getWarrantyInfo = (query) => api.get(`/pos/warranty/info${query}`);

// POS STATUS UPDATE - Handles both string and object
export const updatePOSStatus = (id, statusData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`📤 Updating POS status for ${id}:`, statusData);
  }
  
  // If statusData is a string, send as { status: statusData }
  if (typeof statusData === 'string') {
    return api.put(`/pos/${id}/status`, { status: statusData });
  }
  
  // If it's an object, send as-is
  return api.put(`/pos/${id}/status`, statusData);
};

// ==================== PRODUCTS API ====================
export const getProducts = () => api.get('/products');
export const getProductById = (id) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Fetching product by ID:', id);
  }
  return api.get(`/products/${id}`);
};
export const createProduct = (productData) => api.post('/products', productData);
export const updateProduct = (id, productData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Updating product:', id, productData);
  }
  return api.put(`/products/${id}`, productData);
};
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const getProductByBarcode = (barcode) => api.get(`/products/barcode/${barcode}`);
export const searchProducts = (query) => api.get(`/products/search${query}`);
export const getLowStockProducts = () => api.get('/products/low-stock');
export const getProductCategories = () => api.get('/products/categories');
export const getProductStats = () => api.get('/products/stats');
export const bulkUpdateProducts = (data) => api.put('/products/bulk', data);
export const updateProductStock = (id, data) => api.put(`/products/${id}/stock`, data);
export const toggleProductStatus = (id) => api.put(`/products/${id}/toggle-status`);

// ==================== PRODUCT SERIALS API ====================
/**
 * Get available serials for a product
 * @param {number} productId - Product ID
 * @param {Object} params - Query parameters (status, limit, etc.)
 * @returns {Promise}
 */
export const getProductSerials = (productId, params = {}) => {
  const queryString = buildQueryString(params);
  if (process.env.NODE_ENV !== 'production') {
    console.log(`📤 Fetching serials for product ${productId} with params:`, params);
  }
  return api.get(`/products/${productId}/serials${queryString}`);
};

/**
 * Get all serials for a product
 * @param {number} productId - Product ID
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getAllProductSerials = (productId, params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/products/${productId}/all-serials${queryString}`);
};

/**
 * Add a new serial to a product
 * @param {number} productId - Product ID
 * @param {Object} serialData - Serial data
 * @returns {Promise}
 */
export const addProductSerial = (productId, serialData) => {
  return api.post(`/products/${productId}/serials`, serialData);
};

/**
 * Update serial status
 * @param {number} serialId - Serial ID
 * @param {Object} statusData - Status update data
 * @returns {Promise}
 */
export const updateSerialStatus = (serialId, statusData) => {
  return api.put(`/products/serials/${serialId}/status`, statusData);
};

/**
 * Update serial details
 * @param {number} productId - Product ID
 * @param {number} serialId - Serial ID
 * @param {Object} serialData - Serial data
 * @returns {Promise}
 */
export const updateProductSerial = (productId, serialId, serialData) => {
  return api.put(`/products/${productId}/serials/${serialId}`, serialData);
};

/**
 * Delete a serial
 * @param {number} productId - Product ID
 * @param {number} serialId - Serial ID
 * @returns {Promise}
 */
export const deleteProductSerial = (productId, serialId) => {
  return api.delete(`/products/${productId}/serials/${serialId}`);
};

/**
 * Reserve serials for sale
 * @param {Array} serialIds - Array of serial IDs to reserve
 * @param {string} posTransactionId - POS transaction ID (optional)
 * @returns {Promise}
 */
export const reserveSerials = (serialIds, posTransactionId = null) => {
  return api.post('/products/serials/reserve', { 
    serial_ids: serialIds,
    pos_transaction_id: posTransactionId 
  });
};

/**
 * Search serials by number
 * @param {string} query - Search query
 * @returns {Promise}
 */
export const searchSerials = (query) => {
  return api.get(`/products/serials/search?q=${encodeURIComponent(query)}`);
};

// ==================== PRODUCT ISSUES API ====================
/**
 * Get all product issues with optional filters
 * @param {Object} params - Query parameters (product_id, status, issue_type, severity, start_date, end_date, page, limit)
 * @returns {Promise}
 */
export const getAllProductIssues = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/product-issues${queryString}`);
};

/**
 * Get issues for a specific product
 * @param {number} productId - Product ID
 * @returns {Promise}
 */
export const getProductIssues = (productId) => {
  return api.get(`/product-issues/product/${productId}`);
};

/**
 * Get single issue by ID
 * @param {number} id - Issue ID
 * @returns {Promise}
 */
export const getProductIssueById = (id) => {
  return api.get(`/product-issues/${id}`);
};

/**
 * Create a new product issue
 * @param {Object} issueData - Issue data
 * @returns {Promise}
 */
export const createProductIssue = (issueData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Creating product issue:', issueData);
  }
  return api.post('/product-issues', issueData);
};

/**
 * Update issue details
 * @param {number} id - Issue ID
 * @param {Object} issueData - Issue data
 * @returns {Promise}
 */
export const updateProductIssue = (id, issueData) => {
  return api.put(`/product-issues/${id}`, issueData);
};

/**
 * Update issue status
 * @param {number} id - Issue ID
 * @param {Object} statusData - Status update data
 * @returns {Promise}
 */
export const updateProductIssueStatus = (id, statusData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`📤 Updating issue ${id} status:`, statusData);
  }
  return api.put(`/product-issues/${id}/status`, statusData);
};

/**
 * Delete an issue (only pending issues)
 * @param {number} id - Issue ID
 * @returns {Promise}
 */
export const deleteProductIssue = (id) => {
  return api.delete(`/product-issues/${id}`);
};

/**
 * Resolve an issue
 * @param {number} id - Issue ID
 * @param {Object} resolutionData - Resolution data
 * @returns {Promise}
 */
export const resolveProductIssue = (id, resolutionData) => {
  return api.put(`/product-issues/${id}/resolve`, resolutionData);
};

/**
 * Get product issues statistics
 * @returns {Promise}
 */
export const getProductIssuesStats = () => {
  return api.get('/product-issues/stats');
};

/**
 * Export issues to CSV
 * @param {Object} params - Query parameters
 * @returns {Promise} - Blob response
 */
export const exportProductIssuesToCSV = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/product-issues/export/csv${queryString}`, {
    responseType: 'blob'
  });
};

/**
 * Export issues to PDF
 * @param {Object} params - Query parameters
 * @returns {Promise} - Blob response
 */
export const exportProductIssuesToPDF = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/product-issues/export/pdf${queryString}`, {
    responseType: 'blob'
  });
};

/**
 * Export product summary (Good vs Issue) to CSV
 * @returns {Promise} - Blob response
 */
export const exportProductSummary = () => {
  return api.get('/products/export/summary', {
    responseType: 'blob'
  });
};

/**
 * Export issues report (alias for exportProductIssuesToCSV)
 * @param {Object} params - Query parameters
 * @returns {Promise} - Blob response
 */
export const exportIssuesReport = (params = {}) => {
  const queryString = buildQueryString(params);
  return api.get(`/products/export/issues${queryString}`, {
    responseType: 'blob'
  });
};

// ==================== CUSTOMERS API ====================
export const getCustomers = () => api.get('/customers');
export const getCustomerById = (id) => api.get(`/customers/${id}`);
export const createCustomer = (customerData) => api.post('/customers', customerData);
export const updateCustomer = (id, customerData) => api.put(`/customers/${id}`, customerData);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const searchCustomers = (query) => api.get(`/customers/search${query}`);
export const getCustomerStats = () => api.get('/customers/stats');
export const getActiveCustomers = () => api.get('/customers/active');
export const getCreditCustomers = () => api.get('/customers/credit');

// ==================== INVOICES API ====================
export const getInvoices = (query = '') => api.get(`/invoices${query}`);
export const getInvoiceById = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (invoiceData) => api.post('/invoices', invoiceData);
export const updateInvoice = (id, invoiceData) => api.put(`/invoices/${id}`, invoiceData);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const updateInvoiceStatus = (id, statusData) => api.put(`/invoices/${id}/status`, statusData);
export const markAsPaid = (id) => api.put(`/invoices/${id}/paid`);
export const getInvoiceStats = () => api.get('/invoices/stats');
export const getOverdueInvoices = () => api.get('/invoices/overdue');
export const sendInvoice = (id) => api.put(`/invoices/${id}/send`);
export const printInvoice = (id) => api.get(`/invoices/${id}/print`);
export const downloadInvoice = (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' });
export const sendInvoiceEmail = (id, emailData) => api.post(`/invoices/${id}/send-email`, emailData);

// ==================== REPAIR INVOICE API ====================
export const getRepairs = (query = '') => api.get(`/repairs${query}`);
export const getRepairById = (id) => api.get(`/repairs/${id}`);
export const createRepair = (repairData) => api.post('/repairs', repairData);
export const updateRepair = (id, repairData) => api.put(`/repairs/${id}`, repairData);
export const updateDiagnosis = (id, diagnosisData) => api.put(`/repairs/${id}/diagnosis`, diagnosisData);
export const createRepairQuotation = (id, quotationData) => api.post(`/repairs/${id}/quotation`, quotationData);
export const acceptQuotation = (id, data) => api.post(`/repairs/${id}/accept`, data);
export const addRepairParts = (id, partsData) => api.post(`/repairs/${id}/parts`, partsData);
export const completeRepair = (id, data) => api.put(`/repairs/${id}/complete`, data);
export const recordRepairPayment = (id, paymentData) => api.post(`/repairs/${id}/payment`, paymentData);
export const deliverRepair = (id) => api.put(`/repairs/${id}/deliver`);
export const getRepairStats = () => api.get('/repairs/stats');
export const searchRepairsBySerial = (serial) => api.get(`/repairs/search?serial=${serial}`);
export const getWarrantyRepairs = () => api.get('/repairs/warranty');

// ==================== UTILITY FUNCTIONS ====================
export const getDeviceTypes = () => api.get('/device-types');
export const getRepairCategories = () => api.get('/repair-categories');
export const getTechnicians = () => api.get('/technicians');
export const getSuppliers = () => api.get('/suppliers');

// ==================== SALES API ====================
export const getSales = () => api.get('/sales');
export const getSaleById = (id) => api.get(`/sales/${id}`);
export const createSale = (saleData) => api.post('/sales', saleData);
export const updateSale = (id, saleData) => api.put(`/sales/${id}`, saleData);
export const deleteSale = (id) => api.delete(`/sales/${id}`);
export const updateSaleStatus = (id, statusData) => api.put(`/sales/${id}/status`, statusData);
export const getSalesStats = () => api.get('/sales-stats');
export const getRecentSales = () => api.get('/sales/recent');
export const getSalesByDateRange = (startDate, endDate) => 
  api.get(`/sales/date-range?start=${startDate}&end=${endDate}`);

// ==================== QUOTATIONS API ====================
export const getQuotations = () => api.get('/quotations');
export const getQuotationById = (id) => api.get(`/quotations/${id}`);
export const createQuotation = (quotationData) => api.post('/quotations', quotationData);
export const updateQuotation = (id, quotationData) => api.put(`/quotations/${id}`, quotationData);
export const deleteQuotation = (id) => api.delete(`/quotations/${id}`);
export const updateQuotationStatus = (id, status) => api.put(`/quotations/${id}/status`, { status });
export const convertToInvoice = (id) => api.post(`/quotations/${id}/convert-to-invoice`);
export const convertToSalesOrder = (id) => api.post(`/quotations/${id}/convert-to-sales-order`);
export const getQuotationStats = () => api.get('/quotation-stats');
export const sendQuotation = (id) => api.put(`/quotations/${id}/send`);
export const printQuotation = (id) => api.get(`/quotations/${id}/print`);

// ==================== USERS API ====================
export const getUsers = () => api.get('/users');
export const getUserById = (id) => api.get(`/users/${id}`);
export const createUser = (userData) => api.post('/users', userData);
export const updateUser = (id, userData) => api.put(`/users/${id}`, userData);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const toggleUserStatus = (id) => api.put(`/users/${id}/toggle-status`);
export const getUserStats = () => api.get('/users/stats');
export const updateUserProfile = (id, profileData) => api.put(`/users/${id}/profile`, profileData);
export const changePassword = (id, passwordData) => api.put(`/users/${id}/password`, passwordData);

// ==================== AUTHENTICATION API ====================
export const loginUser = (credentials) => api.post('/auth/login', credentials);
export const logoutUser = () => api.post('/auth/logout');
export const getProfile = () => api.get('/auth/profile');
export const registerUser = (userData) => api.post('/auth/register', userData);
export const verifyToken = () => api.get('/auth/verify');
export const resetPassword = (email) => api.post('/auth/reset-password', { email });
export const changeUserPassword = (data) => api.post('/auth/change-password', data);

// ==================== REPORTS API ====================
export const getSalesReport = (params) => api.get('/reports/sales', { params });
export const getInventoryReport = () => api.get('/reports/inventory');
export const getCustomerReport = () => api.get('/reports/customers');
export const getFinancialReport = (params) => api.get('/reports/financial', { params });
export const getTaxReport = (params) => api.get('/reports/tax', { params });

export const exportReportToCSV = (reportType, params = {}) => {
  return api.get(`/reports/export/${reportType}`, {
    params,
    responseType: 'blob'
  });
};

export const exportReportToPDF = (reportType, params = {}) => {
  return api.get(`/reports/export/${reportType}/pdf`, {
    params,
    responseType: 'blob'
  });
};

// ==================== INVENTORY API ====================
export const getInventory = () => api.get('/inventory');
export const getInventoryById = (id) => api.get(`/inventory/${id}`);
export const updateInventory = (id, inventoryData) => api.put(`/inventory/${id}`, inventoryData);
export const getInventoryTransactions = () => api.get('/inventory/transactions');
export const getStockAlerts = () => api.get('/inventory/alerts');
export const adjustStock = (id, adjustmentData) => api.post(`/inventory/${id}/adjust`, adjustmentData);
export const transferStock = (transferData) => api.post('/inventory/transfer', transferData);

// ==================== CREDIT API ====================
export const recordCreditSale = (data) => api.post('/pos', {
  ...data,
  payment_method: 'credit',
  amount_paid: 0,
  change_amount: 0
});

export const recordPayment = (data) => api.post('/credit/payment', data);
export const getCreditDashboard = () => api.get('/credit/dashboard');
export const getOverdueAccounts = () => api.get('/credit/overdue');
export const generateStatement = (customerId, params) => api.get(`/credit/statements/${customerId}`, { params });
export const getCreditTransactions = (customerId, params) => api.get(`/credit/transactions/${customerId}`, { params });
export const updateCreditLimit = (customerId, data) => api.put(`/customers/${customerId}`, data);
export const testCreditAPI = () => api.get('/credit/test');

// ==================== CATEGORIES API ====================
export const getCategories = () => api.get('/categories');
export const createCategory = (categoryData) => api.post('/categories', categoryData);
export const updateCategory = (id, categoryData) => api.put(`/categories/${id}`, categoryData);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// ==================== SETTINGS API ====================
export const getSettings = () => api.get('/settings');
export const updateSettings = (settingsData) => api.put('/settings', settingsData);
export const getTaxSettings = () => api.get('/settings/tax');
export const updateTaxSettings = (taxData) => api.put('/settings/tax', taxData);
export const getCompanyInfo = () => api.get('/settings/company');
export const updateCompanyInfo = (companyData) => api.put('/settings/company', companyData);

// ==================== FILE UPLOAD API ====================
export const uploadFile = (file, type = 'product') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadProductImage = (productId, file) => {
  const formData = new FormData();
  formData.append('image', file);
  
  return api.post(`/products/${productId}/upload-image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// ==================== SCHOOL PROJECTS API ====================

// Dashboard
export const getSchoolProjectStats = () => api.get('/school-projects/dashboard');

// Projects
export const getSchoolProjects = (params) => api.get('/school-projects/projects', { params });
export const getSchoolProjectById = (id) => api.get(`/school-projects/projects/${id}`);
export const createSchoolProject = (projectData) => api.post('/school-projects/projects', projectData);
export const updateSchoolProject = (id, projectData) => api.put(`/school-projects/projects/${id}`, projectData);
export const deleteSchoolProject = (id) => api.delete(`/school-projects/projects/${id}`);

// Schools
export const getSchools = () => api.get('/school-projects/schools');
export const getSchoolById = (id) => api.get(`/school-projects/schools/${id}`);
export const createSchool = (schoolData) => api.post('/school-projects/schools', schoolData);
export const updateSchool = (id, schoolData) => api.put(`/school-projects/schools/${id}`, schoolData);
export const deleteSchool = (id) => api.delete(`/school-projects/schools/${id}`);

// Computer Parts Management
export const getComputerParts = () => api.get('/school-projects/inventory/parts');
export const getComputerPartById = (id) => api.get(`/school-projects/inventory/parts/${id}`);
export const addComputerPart = (partData) => api.post('/school-projects/inventory/parts', partData);
export const updateComputerPart = (id, partData) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Updating computer part:', id, partData);
  }
  return api.put(`/school-projects/inventory/parts/${id}`, partData);
};
export const deleteComputerPart = (id) => api.delete(`/school-projects/inventory/parts/${id}`);
export const searchComputerParts = (query) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📤 Searching computer parts with query:', query);
  }
  return api.get(`/school-projects/inventory/search?q=${encodeURIComponent(query)}`);
};
export const getLowStockParts = () => api.get('/school-projects/inventory/low-stock');
export const updatePartStock = (id, data) => api.put(`/school-projects/inventory/parts/${id}/stock`, data);
export const getPartCategories = () => api.get('/school-projects/inventory/categories');
export const getPartStats = () => api.get('/school-projects/inventory/stats');

// Inventory Management
export const getSchoolProjectInventory = () => api.get('/school-projects/inventory');
export const getInventoryItemById = (id) => api.get(`/school-projects/inventory/${id}`);
export const updateInventoryItem = (id, inventoryData) => api.put(`/school-projects/inventory/${id}`, inventoryData);
export const getStockAnalysis = () => api.get('/school-projects/inventory/analysis');

// Technicians Management
export const getTechniciansList = () => api.get('/school-projects/technicians');
export const createTechnician = (technicianData) => api.post('/school-projects/technicians', technicianData);
export const getTechnicianById = (id) => api.get(`/school-projects/technicians/${id}`);
export const updateTechnician = (id, technicianData) => api.put(`/school-projects/technicians/${id}`, technicianData);
export const deleteTechnician = (id) => api.delete(`/school-projects/technicians/${id}`);
export const getTechnicianStats = () => api.get('/school-projects/technicians/stats');

// Technician Parts Assignment
export const issuePartsToTechnician = (data) => api.post('/school-projects/technician-parts/issue', data);
export const returnPartsFromTechnician = (data) => api.post('/school-projects/technician-parts/return', data);
export const bulkIssueParts = (data) => api.post('/school-projects/technician-parts/bulk-issue', data);
export const getTechnicianAssignments = (params = {}) => {
  const query = Object.keys(params).length > 0 ? `?${new URLSearchParams(params)}` : '';
  return api.get(`/school-projects/technician-parts/assignments${query}`);
};
export const getAssignmentById = (id) => api.get(`/school-projects/technician-parts/assignments/${id}`);
export const getTechnicianAssignmentHistory = (technicianId) => api.get(`/school-projects/technicians/${technicianId}/assignments`);
export const updateAssignment = (id, data) => api.put(`/school-projects/technician-parts/assignments/${id}`, data);

// Purchases
export const getPurchases = () => api.get('/school-projects/purchases');
export const getPurchaseById = (id) => api.get(`/school-projects/purchases/${id}`);
export const recordPurchase = (purchaseData) => api.post('/school-projects/purchases', purchaseData);
export const updatePurchase = (id, purchaseData) => api.put(`/school-projects/purchases/${id}`, purchaseData);
export const deletePurchase = (id) => api.delete(`/school-projects/purchases/${id}`);

// Reports
export const getSchoolProjectReports = (params) => api.get('/school-projects/reports', { params });
export const getProjectStatusReport = () => api.get('/school-projects/reports/project-status');
export const getInventoryReportSchool = () => api.get('/school-projects/reports/inventory');
export const getFinancialReportSchool = (params) => api.get('/school-projects/reports/financial', { params });

// Computer Management
export const addComputerToProject = (projectId, data) => api.post(`/school-projects/projects/${projectId}/computers`, data);
export const assignPartsToComputer = (computerId, data) => api.post(`/school-projects/computers/${computerId}/assign-parts`, data);
export const updateComputerStatus = (computerId, data) => api.put(`/school-projects/computers/${computerId}`, data);
export const getProjectComputers = (projectId) => api.get(`/school-projects/projects/${projectId}/computers`);
export const getComputerDetails = (computerId) => api.get(`/school-projects/computers/${computerId}`);

// Settings
export const getSchoolProjectSettings = () => api.get('/school-projects/settings');
export const updateSchoolProjectSettings = (settingsData) => api.put('/school-projects/settings', settingsData);

// ==================== UTILITY FUNCTIONS ====================
export const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';
  
  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  
  return query ? `?${query}` : '';
};

export const checkAPIHealth = async () => {
  try {
    const response = await api.get('/test');
    return {
      healthy: true,
      message: 'API is working',
      data: response
    };
  } catch (error) {
    return {
      healthy: false,
      message: error.message || 'API connection failed',
      error
    };
  }
};

export const batchUpdateProducts = (productsData) => api.put('/products/batch', productsData);
export const batchDeleteProducts = (ids) => api.delete('/products/batch', { data: { ids } });

export const posAPI = {
  getStats: getPOSStats,
  create: createPOS,
  getList: getPOS,
  getById: getPOSById,
  refund: refundPOS,
  void: voidPOS,
  searchSerials: searchSerialNumbers,
  getWarranty: getWarrantyInfo,
  updateStatus: updatePOSStatus
};

export const createCreditPOS = (data) => {
  return createPOS({
    ...data,
    payment_method: 'credit',
    amount_paid: 0,
    change_amount: 0
  });
};

// ==================== DEFAULT EXPORT ====================
// Create a default export object with all methods
const apiService = {
  // Test & Debug
  testConnection,
  testPOSConnection,
  debugAPI,
  checkAPIHealth,
  
  // Dashboard
  getDashboardStats,
  
  // POS
  ...posAPI,
  createCreditPOS,
  updatePOSStatus,
  
  // Products
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductByBarcode,
  searchProducts,
  getLowStockProducts,
  getProductCategories,
  getProductStats,
  bulkUpdateProducts,
  updateProductStock,
  toggleProductStatus,
  
  // Product Serials
  getProductSerials,
  getAllProductSerials,
  addProductSerial,
  updateProductSerial,
  deleteProductSerial,
  updateSerialStatus,
  reserveSerials,
  searchSerials,
  
  // Product Issues
  getAllProductIssues,
  getProductIssues,
  getProductIssueById,
  createProductIssue,
  updateProductIssue,
  updateProductIssueStatus,
  deleteProductIssue,
  resolveProductIssue,
  getProductIssuesStats,
  exportProductIssuesToCSV,
  exportProductIssuesToPDF,
  exportProductSummary,
  exportIssuesReport,
  
  // Customers
  getCustomers,
  getCustomerById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  searchCustomers,
  getCustomerStats,
  getActiveCustomers,
  getCreditCustomers,
  
  // Invoices
  getInvoices,
  getInvoiceById,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  updateInvoiceStatus,
  markAsPaid,
  getInvoiceStats,
  getOverdueInvoices,
  sendInvoice,
  printInvoice,
  downloadInvoice,
  sendInvoiceEmail,
  
  // Repair Invoices
  getRepairs,
  getRepairById,
  createRepair,
  updateRepair,
  updateDiagnosis,
  createRepairQuotation,
  acceptQuotation,
  addRepairParts,
  completeRepair,
  recordRepairPayment,
  deliverRepair,
  getRepairStats,
  searchRepairsBySerial,
  getWarrantyRepairs,
  
  // Sales
  getSales,
  getSaleById,
  createSale,
  updateSale,
  deleteSale,
  updateSaleStatus,
  getSalesStats,
  getRecentSales,
  getSalesByDateRange,
  
  // Quotations
  getQuotations,
  getQuotationById,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  updateQuotationStatus,
  convertToInvoice,
  convertToSalesOrder,
  getQuotationStats,
  sendQuotation,
  printQuotation,
  
  // Users
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  toggleUserStatus,
  getUserStats,
  updateUserProfile,
  changePassword,
  
  // Auth
  loginUser,
  logoutUser,
  getProfile,
  registerUser,
  verifyToken,
  resetPassword,
  changeUserPassword,
  
  // Reports
  getSalesReport,
  getInventoryReport,
  getCustomerReport,
  getFinancialReport,
  getTaxReport,
  exportReportToCSV,
  exportReportToPDF,
  
  // Inventory
  getInventory,
  getInventoryById,
  updateInventory,
  getInventoryTransactions,
  getStockAlerts,
  adjustStock,
  transferStock,
  
  // Credit Management
  recordCreditSale,
  recordPayment,
  getCreditDashboard,
  getOverdueAccounts,
  generateStatement,
  getCreditTransactions,
  updateCreditLimit,
  testCreditAPI,
  
  // Categories
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  
  // Settings
  getSettings,
  updateSettings,
  getTaxSettings,
  updateTaxSettings,
  getCompanyInfo,
  updateCompanyInfo,
  
  // File Upload
  uploadFile,
  uploadProductImage,
  
  // Utilities
  getDeviceTypes,
  getRepairCategories,
  getTechnicians,
  getSuppliers,
  buildQueryString,
  
  // School Projects
  getSchoolProjectStats,
  getSchoolProjects,
  getSchoolProjectById,
  createSchoolProject,
  updateSchoolProject,
  deleteSchoolProject,
  getSchools,
  getSchoolById,
  createSchool,
  updateSchool,
  deleteSchool,
  getComputerParts,
  getComputerPartById,
  addComputerPart,
  updateComputerPart,
  deleteComputerPart,
  searchComputerParts,
  getLowStockParts,
  updatePartStock,
  getPartCategories,
  getPartStats,
  getSchoolProjectInventory,
  getInventoryItemById,
  updateInventoryItem,
  getStockAnalysis,
  getTechniciansList,
  createTechnician,
  getTechnicianById,
  updateTechnician,
  deleteTechnician,
  getTechnicianStats,
  issuePartsToTechnician,
  returnPartsFromTechnician,
  bulkIssueParts,
  getTechnicianAssignments,
  getAssignmentById,
  getTechnicianAssignmentHistory,
  updateAssignment,
  getPurchases,
  getPurchaseById,
  recordPurchase,
  updatePurchase,
  deletePurchase,
  getSchoolProjectReports,
  getProjectStatusReport,
  getInventoryReportSchool,
  getFinancialReportSchool,
  addComputerToProject,
  assignPartsToComputer,
  updateComputerStatus,
  getProjectComputers,
  getComputerDetails,
  getSchoolProjectSettings,
  updateSchoolProjectSettings,
  
  // Raw axios instance
  api
};

export default apiService;